import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { Plus, Search, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function RawMaterialsPage() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ material_id: "", quantity: "", supplier: "", notes: "" });

  const loadMaterials = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/raw-materials`);
      const json = await res.json();
      if (json.success) setMaterials(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const handleReceipt = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/factory/raw-materials/receipt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...receiptForm, quantity: parseFloat(receiptForm.quantity) }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Receipt logged. New stock: ${json.new_stock} MT`);
        setShowReceipt(false);
        setReceiptForm({ material_id: "", quantity: "", supplier: "", notes: "" });
        loadMaterials();
      }
    } catch (e) { toast.error("Failed to log receipt"); }
  };

  const filtered = materials.filter(
    (m) => m.name?.toLowerCase().includes(search.toLowerCase()) || m.category?.toLowerCase().includes(search.toLowerCase())
  );
  const criticalCount = materials.filter((m) => m.status === "CRITICAL").length;
  const lowCount = materials.filter((m) => m.status === "LOW").length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="raw-materials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Raw Materials</h1>
          <p className="text-slate-400 text-sm mt-1">Inventory and consumption tracking ({materials.length} items)</p>
        </div>
        <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors" data-testid="add-material-receipt">
              <Plus className="w-4 h-4" /> Log Receipt
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1C2B2B] border-emerald-900/30 text-white">
            <DialogHeader><DialogTitle>Log Material Receipt</DialogTitle></DialogHeader>
            <form onSubmit={handleReceipt} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400">Material</label>
                <select value={receiptForm.material_id} onChange={(e) => setReceiptForm({ ...receiptForm, material_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required>
                  <option value="">Select material</option>
                  {materials.map((m) => <option key={m.material_id} value={m.material_id}>{m.name} ({m.stock_mt} MT)</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Quantity (MT)</label>
                <input type="number" step="0.1" value={receiptForm.quantity} onChange={(e) => setReceiptForm({ ...receiptForm, quantity: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
              </div>
              <div>
                <label className="text-xs text-slate-400">Supplier</label>
                <input value={receiptForm.supplier} onChange={(e) => setReceiptForm({ ...receiptForm, supplier: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Notes</label>
                <input value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" />
              </div>
              <button type="submit" className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="submit-receipt">Log Receipt</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {(criticalCount > 0 || lowCount > 0) && (
        <div className="bg-red-900/20 border border-red-800/30 rounded-xl p-4 flex items-center gap-3" data-testid="material-alerts">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-red-300 text-sm">
            {criticalCount > 0 && <span className="font-semibold">{criticalCount} critical</span>}
            {criticalCount > 0 && lowCount > 0 && " and "}
            {lowCount > 0 && <span className="font-semibold">{lowCount} low stock</span>}
            {" "}materials need attention
          </p>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" placeholder="Search materials..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-600" data-testid="material-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {filtered.map((m) => (
          <div key={m.material_id} className={`bg-[#1C2B2B] border rounded-xl p-4 ${m.status === "CRITICAL" ? "border-red-600/50" : m.status === "LOW" ? "border-amber-600/50" : "border-emerald-900/30"}`} data-testid={`material-card-${m.material_id}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium">{m.name}</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.status === "CRITICAL" ? "bg-red-600/20 text-red-400" : m.status === "LOW" ? "bg-amber-600/20 text-amber-400" : "bg-emerald-600/20 text-emerald-400"}`}>{m.status}</span>
            </div>
            <p className="text-slate-500 text-xs mb-3">{m.category}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Current Stock</span><span className="text-white font-medium">{m.stock_mt?.toLocaleString()} {m.unit}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Daily Usage</span><span className="text-slate-300">{m.daily_consumption} {m.unit}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Days Left</span><span className={`font-medium ${m.days_left < 7 ? "text-red-400" : m.days_left < 10 ? "text-amber-400" : "text-emerald-400"}`}>{m.days_left} days</span></div>
            </div>
            <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${m.status === "CRITICAL" ? "bg-red-500" : m.status === "LOW" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min((m.stock_mt / (m.min_level * 3)) * 100, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
